import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, ShieldCheck } from 'lucide-react';

const TESTS = [
{
  key: 'repeatability',
  name: 'Repeatability',
  question: 'Has this or something very similar been reported before?',
  signalIndicator: 'Multiple independent reports of similar friction'
},
{
  key: 'impact_magnitude',
  name: 'Impact Magnitude',
  question: 'Did this issue cause measurable damage to a mission or objective?',
  signalIndicator: 'Clear degradation of activities, tasks and their outcomes'
},
{
  key: 'causal_clarity',
  name: 'Causal Clarity',
  question: 'Can we trace this to a specific gap in officer, artifact, policy, or capability?',
  signalIndicator: 'Clean causal chain from gap to impact'
},
{
  key: 'breadth',
  name: 'Breadth',
  question: 'Is this isolated to one officer/team, or appearing across multiple?',
  signalIndicator: 'Cross-cutting appearance across contexts'
},
{
  key: 'escalation_trend',
  name: 'Escalation Trend',
  question: 'Is this getting worse over time or staying flat?',
  signalIndicator: 'Worsening trajectory'
},
{
  key: 'strategic_relevance',
  name: 'Tactical / Strategic Relevance',
  question: 'Does this issue touch something connected to current Tactics or Strategy?',
  signalIndicator: 'Direct connection to strategic and tactical priorities'
}];


export default function SignalDetectionPanel({ issue, onComplete }) {
  const queryClient = useQueryClient();

  const savedTests = issue?.gate1_tests || {};
  const [tests, setTests] = useState({
    repeatability: savedTests.repeatability || false,
    impact_magnitude: savedTests.impact_magnitude || false,
    causal_clarity: savedTests.causal_clarity || false,
    breadth: savedTests.breadth || false,
    escalation_trend: savedTests.escalation_trend || false,
    strategic_relevance: savedTests.strategic_relevance || false
  });
  const [dissentNotes, setDissentNotes] = useState(issue?.dissent_notes || '');

  const score = Object.values(tests).filter(Boolean).length;

  const getScoreColor = () => {
    if (score <= 1) return 'text-emerald-400';
    if (score === 2) return 'text-amber-400';
    return 'text-red-400';
  };

  const getScoreBgColor = () => {
    if (score <= 1) return 'bg-emerald-500/10 border-emerald-500/30';
    if (score === 2) return 'bg-amber-500/10 border-amber-500/30';
    return 'bg-red-500/10 border-red-500/30';
  };

  const getRecommendation = () => {
    if (score <= 1) return 'Recommendation: Archive as Noise';
    if (score === 2) return 'Recommendation: Borderline — discuss before classifying';
    return 'Recommendation: Classify as Signal — advance to Gate 2';
  };

  const classifyMutation = useMutation({
    mutationFn: async ({ classification }) => {
      const user = await base44.auth.me();
      const now = new Date().toISOString();
      const data = {
        classification_gate1: classification,
        gate1_score: score,
        gate1_tests: tests,
        gate1_classified_at: now,
        classified_by: user.email,
        classified_at: now,
        dissent_notes: dissentNotes || null
      };
      if (classification === 'noise') {
        data.status = 'archived';
      } else {
        data.status = 'under_review';
      }
      return base44.entities.Issue.update(issue.id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      onComplete?.();
    }
  });

  return (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <h3 className="text-slate-600 text-lg font-bold flex items-center justify-center gap-2">Signal Detection Tests


        </h3>
        <p className="text-xs text-slate-500 mt-1">Evaluate each diagnostic to determine Noise vs. Signal</p>
      </div>

      {/* Test Rows */}
      <div className="space-y-1">
        {TESTS.map((test) =>
        <div key={test.key} className="bg-gray-50 p-3 rounded-lg border border-slate-700/50">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-slate-600 text-sm font-semibold">{test.name}</p>
                <p className="text-slate-800 mt-0.5 text-xs">{test.question}</p>
                {tests[test.key] &&
              <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" />
                    {test.signalIndicator}
                  </p>
              }
              </div>
              <Switch
              checked={tests[test.key]}
              onCheckedChange={(checked) => setTests((prev) => ({ ...prev, [test.key]: checked }))} className="bg-slate-500 rounded-full peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input" />

            </div>
          </div>
        )}
      </div>

      {/* Score */}
      <div className={`rounded-lg border p-4 text-center ${getScoreBgColor()}`}>
        <p className={`text-2xl font-bold ${getScoreColor()}`}>
          Signal Score: {score} / 6
        </p>
        <p className={`text-sm mt-1 ${getScoreColor()}`}>
          {getRecommendation()}
        </p>
      </div>

      {/* Dissent Notes */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-slate-400">Dissent Notes (if split decision)</label>
        <Textarea data-testid="issues-signaldetectionpanel-textarea"
          value={dissentNotes}
          onChange={(e) => setDissentNotes(e.target.value)} className="bg-gray-50 text-slate-700 px-3 py-2 text-sm rounded-md flex min-h-[60px] w-full border shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm border-slate-700 placeholder:text-slate-600"

          rows={2}
          placeholder="Record any dissenting views from the council..." />

      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          variant="outline" className="bg-background text-slate-700 px-4 py-2 text-sm font-medium rounded-md inline-flex items-center justify-center gap-2 whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border shadow-sm h-9 flex-1 border-slate-600 hover:bg-slate-800 hover:text-slate-100"

          onClick={() => classifyMutation.mutate({ classification: 'noise' })}
          disabled={classifyMutation.isPending}>

          Classify as Noise
        </Button>
        <Button
          className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
          onClick={() => classifyMutation.mutate({ classification: 'signal' })}
          disabled={classifyMutation.isPending}>

          Classify as Signal
        </Button>
      </div>
    </div>);

}